import React, { Component } from "react";
import { toast } from "react-toastify";
import API from "../utils/api";
import { getCallStatusLabel } from "../utils/callStatusCache";
import { formatDate } from "../utils/dateFormatter";
import LeadForm from "../components/LeadForm";

class LeadEditPage extends Component {
  state = {
    lead: null,
    loading: true,
    error: null,
    assigning: true,
    assignedData: null,
    redirectedLeadId: null,
  };

  componentDidMount() {
    this.assignAndFetchLead();
  }

  assignAndFetchLead = async () => {
    try {
      const { id } = this.props.match.params;

      // First, assign the lead to current user (returns the assigned lead's full data)
      let assignedData = null;
      try {
        const assignRes = await API.post(`/lead/assign/${id}`);
        assignedData = assignRes.data.data || assignRes.data;
      } catch (assignErr) {
        const msg =
          assignErr.response && assignErr.response.data && assignErr.response.data.message
            ? assignErr.response.data.message
            : "Failed to assign lead";

        // For any error (e.g., 403, 404, etc.), show an error message that redirects to leads list on click
        toast.error(`${msg}. Click to go back to leads list.`, {
          onClick: () => this.props.history.push("/leads"),
          autoClose: 5000,
        });
        this.setState({ error: msg, loading: false, assigning: false });
        return;
      }

      // The assign endpoint returns the correct (possibly previously-assigned) lead's full data.
      // If the assigned lead is different from the URL, show the assigned lead with a warning.
      let redirectedLeadId = null;
      if (assignedData && assignedData.id && String(assignedData.id) !== String(id)) {
        redirectedLeadId = assignedData.id;
        toast.warn("You already have lead #" + assignedData.id + " assigned. Complete it before opening another.");
      }

      // Use lead data from the assign response if available, otherwise fetch separately
      let lead = assignedData;
      if (!lead || !lead.id) {
        const res = await API.get(`/lead/${id}`);
        const data = res.data.data || res.data;
        lead = Array.isArray(data) ? data[0] : data.leads ? data.leads[0] : data;
      }

      if (!lead) {
        this.setState({ error: "Lead not found", loading: false, assigning: false });
        return;
      }

      this.setState({
        lead,
        assignedData,
        redirectedLeadId,
        loading: false,
        assigning: false,
      });
    } catch (e) {
      toast.error("Failed to load lead");
      this.setState({ error: "Failed to load lead", loading: false, assigning: false });
    }
  };

  render() {
    const { lead, loading, error, assigning, assignedData, redirectedLeadId } = this.state;
    const displayLeadId = redirectedLeadId || this.props.match.params.id;

    if (loading || assigning) {
      return (
        <div>
          <h2 className="page-header">Edit Lead</h2>
          <p>
            <i className="fas fa-spinner fa-spin"></i> {assigning ? "Assigning lead..." : "Loading lead..."}
          </p>
        </div>
      );
    }

    if (error) {
      return (
        <div>
          <h2 className="page-header">Edit Lead</h2>
          <div className="alert alert-danger">{error}</div>
          <button className="btn btn-default" onClick={() => this.props.history.goBack()}>
            <i className="fas fa-arrow-left"></i> Go Back
          </button>
        </div>
      );
    }

    return (
      <div>
        <h2 className="page-header">Edit Lead #{displayLeadId}</h2>
        {redirectedLeadId && (
          <div className="alert alert-warning">
            <i className="fas fa-exclamation-triangle"></i> You already have lead <strong>#{redirectedLeadId}</strong> assigned. Please complete it before opening another lead.
          </div>
        )}
        {!redirectedLeadId && assignedData && assignedData.isAlreadyAssigned && (
          <div className="alert alert-warning">
            <i className="fas fa-exclamation-triangle"></i> You already have this lead assigned. Please update it before opening another lead.
          </div>
        )}
        {/* Assignment History */}
        {assignedData && assignedData.assignedHistory && assignedData.assignedHistory.length > 0 && (
          <div className="panel panel-default" style={{ marginBottom: 15 }}>
            <div className="panel-heading"><strong>Previous Assignment History</strong></div>
            <table className="table table-bordered table-condensed" style={{ margin: 0 }}>
              <thead>
                <tr>
                  <th>Executive</th>
                  <th>Call Status</th>
                  <th>Remark</th>
                  <th>Date</th>
                  <th>Recordings</th>
                </tr>
              </thead>
              <tbody>
                {assignedData.assignedHistory.map((h, i) => (
                  <tr key={i}>
                    <td>{h.name}</td>
                    <td>
                      <span className="label label-info">
                        {getCallStatusLabel(h.call_status)}
                      </span>
                    </td>
                    <td>{h.remark || "-"}</td>
                    <td>{formatDate(h.updated_at)}</td>
                    <td>
                      <RecordingCell
                        leadId={lead.id}
                        allocationId={h.allocation_id}
                        recordingUrls={h.recording_urls}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <LeadForm
          isEdit={true}
          leadId={displayLeadId}
          lead={lead}
          history={this.props.history}
        />
      </div>
    );
  }
}

export default LeadEditPage;

const formatTime = (secs) => {
  if (!secs || isNaN(secs)) return "0:00";
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

const RecordingCell = ({ leadId, allocationId, recordingUrls }) => {
  const [expanded, setExpanded] = React.useState(false);
  const [presignedUrls, setPresignedUrls] = React.useState({});
  const [loading, setLoading] = React.useState({});
  const [playingKey, setPlayingKey] = React.useState(null);
  const [paused, setPaused] = React.useState(false);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [duration, setDuration] = React.useState(0);
  const audioRef = React.useRef(null);

  let urls = [];
  if (recordingUrls) {
    try {
      urls = typeof recordingUrls === "string" ? JSON.parse(recordingUrls) : recordingUrls;
    } catch (_) {
      urls = [];
    }
  }

  if (!urls || urls.length === 0) return <span className="text-muted">-</span>;

  const fetchPresignedUrl = async (key) => {
    if (presignedUrls[key]) return presignedUrls[key];
    setLoading((prev) => ({ ...prev, [key]: true }));
    try {
      const res = await API.post("/recording/presigned-url", {
        key,
        lead_id: leadId,
        allocation_id: allocationId,
      });
      const url = res.data.data.url;
      setPresignedUrls((prev) => ({ ...prev, [key]: url }));
      return url;
    } catch (e) {
      console.error("Failed to fetch presigned URL for recording", e);
      // toast imported at top of file
      toast.error("Failed to fetch recording URL");
      return null;
    } finally {
      setLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingKey(null);
    setPaused(false);
    setCurrentTime(0);
    setDuration(0);
  };

  const handlePlay = async (key) => {
    if (playingKey === key) {
      if (paused) {
        audioRef.current.play();
        setPaused(false);
      } else {
        audioRef.current.pause();
        setPaused(true);
      }
      return;
    }

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingKey(null);
    setPaused(false);
    setCurrentTime(0);
    setDuration(0);

    const url = await fetchPresignedUrl(key);
    if (!url) return;

    const audio = new Audio(url);
    audioRef.current = audio;

    audio.addEventListener("loadedmetadata", () => setDuration(audio.duration));
    audio.addEventListener("timeupdate", () => setCurrentTime(audio.currentTime));
    audio.addEventListener("ended", stopAudio);
    audio.addEventListener("error", () => { toast.error("Failed to play recording"); stopAudio(); });

    setPlayingKey(key);
    setPaused(false);
    audio.play();
  };

  const handleSeek = (e) => {
    if (!audioRef.current || !duration) return;
    const val = Number(e.target.value);
    audioRef.current.currentTime = val;
    setCurrentTime(val);
  };

  const handleDownload = async (key, index) => {
    const url = await fetchPresignedUrl(key);
    if (!url) return;
    const fileName = key.split("/").pop() || `recording_${index + 1}.m4a`;
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div style={{ minWidth: 160 }}>
      <button
        className="btn btn-xs btn-info"
        onClick={() => setExpanded(!expanded)}
        title="Show recordings"
      >
        <i className={`fas fa-microphone${expanded ? "-slash" : ""}`}></i>{" "}
        {urls.length} recording{urls.length !== 1 ? "s" : ""}
      </button>
      {expanded && (
        <div style={{ marginTop: 6 }}>
          {urls.map((key, index) => {
            const filename = key.split("/").pop() || `Recording ${index + 1}`;
            const isLoading = loading[key];
            const isActive = playingKey === key;
            const isPlaying = isActive && !paused;
            return (
              <div key={key} style={{ marginBottom: 8, borderBottom: "1px solid #eee", paddingBottom: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "nowrap" }}>
                  <span
                    style={{ fontSize: 11, color: "#555", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 110 }}
                    title={filename}
                  >
                    {index + 1}. {filename}
                  </span>
                  <button
                    className={`btn btn-xs ${isPlaying ? "btn-warning" : "btn-success"}`}
                    onClick={() => handlePlay(key)}
                    disabled={isLoading}
                    title={isPlaying ? "Pause" : "Play"}
                  >
                    {isLoading ? (
                      <i className="fas fa-spinner fa-spin"></i>
                    ) : (
                      <i className={`fas fa-${isPlaying ? "pause" : "play"}`}></i>
                    )}
                  </button>
                  {isActive && (
                    <button className="btn btn-xs btn-danger" onClick={stopAudio} title="Stop">
                      <i className="fas fa-stop"></i>
                    </button>
                  )}
                  {/* <button
                    className="btn btn-xs btn-default"
                    onClick={() => handleDownload(key, index)}
                    disabled={isLoading}
                    title="Download"
                  >
                    {isLoading ? (
                      <i className="fas fa-spinner fa-spin"></i>
                    ) : (
                      <i className="fas fa-download"></i>
                    )}
                  </button> */}
                </div>
                {isActive && (
                  <div style={{ marginTop: 4 }}>
                    <input
                      type="range"
                      min={0}
                      max={duration || 0}
                      step={0.1}
                      value={currentTime}
                      onChange={handleSeek}
                      style={{ width: "100%", cursor: "pointer", accentColor: "#5bc0de" }}
                    />
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#888", marginTop: 1 }}>
                      <span>{formatTime(currentTime)}</span>
                      <span>{formatTime(duration)}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
